import { getLocalPosition } from "./LocalPosition";
import { drawRouteFromLocation, clearRouteBeforeResponse } from "./RouteDrawing";
import { checkForFatalError, checkDuplicateLocation, compareNewData } from "./Validations";
import { geoCoding, createMapMarker } from "./Markers";
import { WEBUILogs } from "@tinqin/tinqin-utils/src/logging";

function checkComponentUnmount(getState, pathInStore, widgetId) {
    return getState().getIn(["data", widgetId, ...pathInStore]);
}

export function determineDelayedRequestsSliceTo(sliceToOldValue, locations) {
    let sliceToNewValue = 0;
    if (sliceToOldValue + 8 >= locations.length - 1) {
        sliceToNewValue = locations.length;
    } else {
        sliceToNewValue = sliceToOldValue + 8;
    }

    return sliceToNewValue;
}

export function resolvedPromiseArrayCallback(params) {
    return new Promise((resolve, reject) => {
        Promise.all(params.allRequests).then(responseValues => {
            let resolvedResponseValues = responseValues;
            let mapCenter = null;
            let fitBounds = false;
            let zoom = false;
            if (
                params.targetCurrentLocation &&
                !params.drawRouteFlag &&
                !params.markersReference.length &&
                !params.specificLocation &&
                !params.drawClosest
            ) {
                zoom = true;
                getLocalPosition(params.errorMessages, params.apiKey).then(
                    response => {
                        resolve({ mapCenter: response, render: true, zoom: zoom });
                    },
                    error => {
                        resolve({
                            mapCenter: { lat: -28.024, lng: 140.887 },
                            render: true,
                            zoom: zoom
                        });
                        WEBUILogs.error(error);
                    }
                );
                return;
            }

            // We are targeting the specific marker / geo location
            if (params.specificLocation) {
                let index = params.markersReference
                    .map(e => {
                        return e.id;
                    })
                    .indexOf(params.specificLocation);

                if (index !== -1) {
                    let marker = params.markersReference[index];
                    mapCenter = marker.position;
                    zoom = true;
                    if (marker.popoverContent) {
                        params.infowindowRef.setContent(marker.popoverContent);
                        params.infowindowRef.open(params.map, marker);
                    }
                }
            }

            // All the request has completed and all the callback promises has been resolved.
            // We fit the screen to match all the markers. This is done only we are not drawing directions route.
            if (!params.specificLocation && !params.drawClosest) {
                params.infowindowRef.close();
                if (params.markersReference.length === 1) {
                    //using setCenter instead of panTo
                    //for issues with initial map display and panTo
                    //http://jira.tinqin.com/browse/AUR-10675
                    mapCenter = params.markersReference[0].position;
                    zoom = true;
                } else {
                    fitBounds = true;
                }
            }

            if (params.drawClosest && params.markersReference && params.markersReference.length) {
                drawRouteFromLocation({
                    map: params.map,
                    directions: { mode: params.directions.mode },
                    errorMessages: params.errorMessages,
                    markersReference: params.markersReference,
                    directionsDisplayRef: params.directionsDisplayRef,
                    apiKey: params.apiKey
                }).then(response => {
                    let fatalErrorCheck = checkForFatalError({
                        locations: params.locations,
                        markersReference: params.markersReference,
                        drawRouteFlag: response.routed,
                        specificLocation: params.specificLocation,
                        directionsDisplayRef: response.directionsDisplayRef,
                        map: params.map,
                        infowindowRef: params.infowindowRef
                    });

                    resolve({
                        fitBounds: fitBounds,
                        mapCenter: mapCenter,
                        resolvedResponseValues: resolvedResponseValues,
                        zoom: zoom,
                        ...fatalErrorCheck
                    });
                });
            } else {
                let clearedRoute = clearRouteBeforeResponse(
                    params.drawRouteFlag,
                    params.directionsDisplayRef
                );

                let fatalErrorCheck = checkForFatalError({
                    locations: params.locations,
                    markersReference: params.markersReference,
                    drawRouteFlag: params.drawRouteFlag,
                    specificLocation: params.specificLocation,
                    directionsDisplayRef: clearedRoute,
                    map: params.map,
                    infowindowRef: params.infowindowRef
                });

                resolve({
                    fitBounds: fitBounds,
                    mapCenter: mapCenter,
                    resolvedResponseValues: resolvedResponseValues,
                    zoom: zoom,
                    ...fatalErrorCheck
                });
            }
        });
    });
}

export function fillPromiseRequests(params) {
    for (let i = 0; i < params.requestLocations.length; i++) {
        // We are skipping the duplicate(the are present in the directions and the locations) geo locations if any.
        if (
            checkDuplicateLocation({
                drawRouteFlag: params.drawRouteFlag,
                directions: params.directions,
                location: params.requestLocations[i].location
            })
        ) {
            continue;
        }

        if (
            params.markersReference.length &&
            !compareNewData(params.requestLocations[i].location, params.markersReference)
        ) {
            continue;
        }

        if (typeof params.requestLocations[i].location === "string") {
            params.allRequests.push(
                geoCoding({
                    location: params.requestLocations[i],
                    markerDropAnimation: params.markerDropAnimation,
                    bounds: params.bounds,
                    geocoder: params.geocoder,
                    map: params.map,
                    errorMessages: params.errorMessages,
                    markersReference: params.markersReference,
                    showLocationOnMapsLink: params.showLocationOnMapsLink,
                    markerActionProps: params.markerActionProps,
                    dispatch: params.dispatch
                })
            );
        } else if (
            params.requestLocations[i].location.hasOwnProperty("lat") &&
            params.requestLocations[i].location.hasOwnProperty("lng")
        ) {
            let LngLatMarker = createMapMarker(params.requestLocations[i].location, {
                location: params.requestLocations[i],
                markerDropAnimation: params.markerDropAnimation,
                showLocationOnMapsLink: params.showLocationOnMapsLink,
                map: params.map,
                markerActionProps: params.markerActionProps,
                dispatch: params.dispatch
            });
            params.markersReference.push(LngLatMarker);
            params.allRequests.push(Promise.resolve({ status: "OK", marker: LngLatMarker }));
        }
    }
}

export function delayedRequests(params, resolver) {
    let unmountedComponent = checkComponentUnmount(
        params.getState,
        params.markerActionProps.pathInStore,
        params.markerActionProps.widgetId
    );

    if (unmountedComponent) {
        params.allRequests = [];
        fillPromiseRequests(params);

        resolvedPromiseArrayCallback(params).then(response => {
            let timeoutDelay = params.timeoutDelay || 2000;
            setTimeout(() => {
                params.response = { ...response } || {};
                let requestLocations = [];
                let rejectedResponseValues = response.resolvedResponseValues;
                let sliceToNewValue = determineDelayedRequestsSliceTo(
                    params.sliceTo,
                    params.locations
                );
                let endRecursionFlag = false;
                if (params.sliceTo === sliceToNewValue) {
                    endRecursionFlag = true;
                }

                for (let i = 0; i < rejectedResponseValues.length; i++) {
                    if (rejectedResponseValues[i].hasOwnProperty("location")) {
                        requestLocations.push(rejectedResponseValues[i].location);
                    }
                }

                if (requestLocations.length && requestLocations.length < 8) {
                    let additionalLocations = params.sliceTo + (8 - requestLocations.length);
                    if (additionalLocations > params.locations.length) {
                        additionalLocations = params.locations.length;
                    }

                    params.timeoutDelay = requestLocations.length * 2000;

                    for (let i = params.sliceTo; i < additionalLocations; i++) {
                        requestLocations.push(params.locations[i]);
                    }
                    params.sliceTo = additionalLocations;
                    params.requestLocations = requestLocations;
                } else if (!requestLocations.length) {
                    let requestLocations = params.locations.slice(params.sliceTo, sliceToNewValue);
                    params.sliceTo = sliceToNewValue;
                    params.requestLocations = requestLocations;
                }

                if (endRecursionFlag && !requestLocations.length) {
                    resolver(params.response);
                } else {
                    delayedRequests(params, resolver);
                }
            }, timeoutDelay);
        });
    }
}
